import { ExcelButton } from "@/controls/buttons/Excel-button";
import { HeatSearch } from "@/controls/heatSearch/components/HeatSearchTextField";
import { ATHENS_GRAY } from "@/controls/shared/constants/colors";
import {
	ErrorModal,
	HelmetDexteel,
	ShiftDayNavigatorControl,
	ShiftInfo,
	getMomentTz,
	useShiftNavigator,
} from "@dexteel/mesf-core";
import {
	Box,
	Checkbox,
	FormControlLabel,
	FormGroup,
	Grid2 as Grid,
	Paper,
	Theme,
	Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { ProcessCellForExportParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { get } from "lodash-es";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { HeatsByShiftTable } from "./components/HeatsByShiftTable";
import { useGradeContext } from "./context/GradesContext";
import { useHeatsByShiftContext } from "./context/HeatsByShiftContext";
import { useLadleContext } from "./context/LadlesContext";
import {
	getGrades,
	getHeatsByShift,
	getLadles,
	getLastestHeats,
	searchHeats,
} from "./repositories/heatsRepository";
const useStyles = makeStyles((theme: Theme) => ({
	label: {
		fontSize: "14px",
	},
}));

const moment = getMomentTz();
export const HeatsDataEntryPage = () => {
	const navigate = useNavigate();
	const classes = useStyles();
	const {
		state: { heats, lastestHeats },
		actions: { setHeatsByShift, setLastestHeats },
	} = useHeatsByShiftContext();
	const {
		actions: { setLadles },
	} = useLadleContext();
	const {
		actions: { setGrades },
	} = useGradeContext();

	const { shiftInfo, moveShift, getShiftDataFromAPI, initShiftNavigator } =
		useShiftNavigator();
	const shiftDate = moment(shiftInfo?.CurrentStart).format("MM-DD-YYYY");

	const [error, setError] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [searchParams, setSearchParams] = useSearchParams();

	const externalHeatsQueryParam = searchParams.get("externalHeats");
	const initialShowExternalsHeats = externalHeatsQueryParam === "true";

	const [showExternalsHeats, setShowExternalsHeats] = useState(
		initialShowExternalsHeats,
	);

	const [firstRender, setFirstRender] = useState(true);
	const { shiftId: shiftIdParam } = useParams();
	const gridRef = useRef<AgGridReact>(null);

	const handleContainerContextMenu = (
		event: React.MouseEvent<HTMLDivElement, MouseEvent>,
	) => {
		event.preventDefault();
	};
	const getHeatsByShiftFromAPI = async (shift?: ShiftInfo) => {
		setIsLoading(true);
		const shiftId = get(shift ? shift : shiftInfo, "CurrentShiftId", null);
		const heatsResp = await getHeatsByShift(
			Number(shiftId),
			null,
			showExternalsHeats,
		);
		if (heatsResp.ok) {
			setHeatsByShift(heatsResp.data.tables[0].rows);
		} else {
			setError(heatsResp.message);
		}
		setIsLoading(false);
	};

	const getLastestHeatsFromAPI = async () => {
		setIsLoading(true);
		const lastestHeatsResp = await getLastestHeats(showExternalsHeats);
		if (lastestHeatsResp.ok) {
			const data = get(lastestHeatsResp, "data.tables[0].rows", null);
			setLastestHeats(data);
		} else {
			setError(lastestHeatsResp.message);
		}
		setIsLoading(false);
	};
	const handleShowExternalsHeats = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const isChecked = event.target.checked;
		setShowExternalsHeats(isChecked);

		const newSearchParams = new URLSearchParams(searchParams.toString());
		if (isChecked) {
			newSearchParams.set("externalHeats", "true");
		} else {
			newSearchParams.delete("externalHeats");
		}
		setSearchParams(newSearchParams);
	};
	const getHeats = async (searchTerm: string) => {
		if (searchTerm.length >= 3) {
			const searchHeatsResp = await searchHeats(searchTerm, showExternalsHeats);
			if (searchHeatsResp.ok) {
				const data = get(searchHeatsResp, "data.tables[0].rows", null);
				return data;
			} else {
				setError(searchHeatsResp.message);
				return [];
			}
		} else {
			return lastestHeats;
		}
	};

	const getCatalogs = async () => {
		const [ladleResp, gradesResp] = await Promise.all([
			getLadles(),
			getGrades(),
		]);

		if (ladleResp.ok) {
			setLadles(ladleResp.data?.tables[0].rows);
		} else {
			setError(ladleResp.message);
		}
		if (gradesResp.ok) {
			setGrades(gradesResp.data.tables[0].rows);
		} else {
			setError(gradesResp.message);
		}
	};
	const handleExportToExcel = () => {
		if (gridRef.current && gridRef.current.api) {
			const params = {
				skipHeader: false,
				skipFooters: true,
				skipGroups: true,
				fileName: `Heats-${shiftDate}.xlsx`,
				sheetName: "Heats by shift",
				processCellCallback: (params: ProcessCellForExportParams) => {
					const val = params.value;
					return val ?? "";
				},
			};
			gridRef.current.api.exportDataAsExcel(params);
		} else {
			setError("Unable to export data. Please try again.");
		}
	};
	useEffect(() => {
		if (shiftInfo && !firstRender) {
			const shiftId = get(shiftInfo, "CurrentShiftId", null);
			const newSearchParams = new URLSearchParams(searchParams.toString());

			if (showExternalsHeats) {
				newSearchParams.set("externalHeats", "true");
			} else {
				newSearchParams.delete("externalHeats");
			}
			navigate(`/meltshop-heats/${shiftId}?${newSearchParams.toString()}`);

			(async () => {
				await getHeatsByShiftFromAPI();
				await getLastestHeatsFromAPI();
			})();
		}
	}, [shiftInfo, showExternalsHeats]);

	useEffect(() => {
		if (shiftIdParam) {
			(async () => {
				const shiftId = Number(shiftIdParam);
				getShiftDataFromAPI(null, shiftId).then(async (shift) => {
					if (shift) {
						getCatalogs().then(() => {
							getHeatsByShiftFromAPI(shift).then(() => {
								getLastestHeatsFromAPI();
								setFirstRender(false);
							});
						});
					}
				});
			})();
		} else {
			(async () => {
				getShiftDataFromAPI(null, 0).then(async (shift) => {
					if (shift) {
						getCatalogs().then(() => {
							getHeatsByShiftFromAPI(shift).then(() => {
								getLastestHeatsFromAPI();
								setFirstRender(false);
							});
						});
					}
				});
			})();
		}
	}, []);

	return (
		<>
			<HelmetDexteel title={`Heats`} />
			<div onContextMenu={handleContainerContextMenu}>
				<Paper
					elevation={0}
					style={{
						width: "100%",
						padding: "15px 20px 10px",
						height: "93vh",
						backgroundColor: ATHENS_GRAY,
					}}
				>
					<Grid container justifyContent="center" alignItems="center">
						<Grid size={{ xs: 12, md: 12 }} style={{ margin: 0 }}>
							<Grid size={{ xs: 12, md: 12 }}>
								<Grid
									container
									direction="row"
									justifyContent="space-between"
									alignItems="center"
								>
									<Typography variant="h5" style={{ fontWeight: 600 }}>
										Heats
									</Typography>
								</Grid>
							</Grid>
							<Grid size={{ xs: 12, md: 12 }} style={{ marginBottom: "10px" }}>
								<Grid
									container
									direction="row"
									justifyContent="flex-start"
									alignItems="stretch"
									spacing={1}
								>
									<Grid size={{ xs: "auto" }} style={{ display: "flex" }}>
										<Paper
											elevation={1}
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
											}}
										>
											<ShiftDayNavigatorControl preventInit withPaper={false} />
										</Paper>
									</Grid>
									<Grid size={{ xs: "auto" }}>
										<Paper
											style={{
												minWidth: "400px",
												display: "flex",
												alignItems: "center",
											}}
										>
											<Box sx={{ p: 1 }}>
												<Grid
													container
													spacing={2}
													justifyContent="center"
													alignItems="center"
												>
													<Grid size={{ xs: 6 }}>
														<HeatSearch
															heats={lastestHeats}
															getHeats={getHeats}
															loading={isLoading}
															onBlur={() => {}}
															onChange={(heat) => {
																const newSearchParams = new URLSearchParams();
																if (heat.Id) {
																	newSearchParams.set(
																		"heatId",
																		heat.Id.toString(),
																	);
																}
																if (showExternalsHeats) {
																	newSearchParams.set("externalHeats", "true");
																}
																const queryString = newSearchParams.toString();
																window.location.href = `/meltshop-heats/${heat.ShiftId}?${queryString}`;
															}}
														/>
													</Grid>
													<Grid size={{ xs: 6 }}>
														<FormGroup>
															<FormControlLabel
																control={
																	<Checkbox
																		checked={showExternalsHeats}
																		color="primary"
																		onChange={handleShowExternalsHeats}
																	/>
																}
																label="Show external heats"
																classes={{
																	label: classes.label,
																}}
															/>
														</FormGroup>
													</Grid>
												</Grid>
											</Box>
										</Paper>
									</Grid>
									<Grid
										size={{ xs: "auto" }}
										style={{
											display: "flex",
											alignItems: "center",
											marginLeft: "auto",
										}}
									>
										<ExcelButton onClick={handleExportToExcel} />
									</Grid>
								</Grid>
							</Grid>
							<HeatsByShiftTable
								isLoading={isLoading}
								getHeatsByShift={getHeatsByShiftFromAPI}
								gridRef={gridRef}
							/>
						</Grid>
					</Grid>
				</Paper>
				<ErrorModal error={error} onHide={() => setError("")} />
			</div>
		</>
	);
};
